import * as styles from "./ScheduleSection.css";

import { useAtom } from "jotai";
import type { JSX } from "react";

import {
  Field,
  FieldContent,
  FieldContext,
  FieldError,
  FieldLabel,
  makeFieldContext,
} from "@frontend/components/atoms/Field";
import {
  Slider,
  SliderControl,
  SliderIndicator,
  SliderLabel,
  SliderMarks,
  SliderThumb,
  SliderTrack,
  SliderValue,
} from "@frontend/components/atoms/Slider";
import {
  TimePicker,
  TimePickerContent,
  TimePickerTrigger,
  TimePickerValue,
} from "@frontend/components/atoms/TimePicker";
import { useField } from "@frontend/hooks/form";
import { useIntlLocale } from "@frontend/hooks/useIntlLocale";
import { HoursField } from "@frontend/shared/hours-field";
import { Trans, useLingui } from "@lingui/react/macro";

import { projectForm } from "../stores/editor";
import { SectionFrame } from "./Section";

const intervention = projectForm.fields.intervention.fields;

const schedule = intervention.dailySchedule.fields;

const STEP = 5;

// Out of the section's grouped grid: the label and the times on one line,
// the times taking only the room they need.
const inline = makeFieldContext({
  orientation: "horizontal",
  labelAlign: "left",
  grouped: false,
  errorDisplay: "tooltip",
});

const RIDE_MAX = 180;

// A tick every ten minutes, a word at every hour.
const RIDE_MARKS = Array.from({ length: RIDE_MAX / 10 }, (_, index) => {
  const value = (index + 1) * 10;

  return 0 === value % 60 ? { value, label: `${value / 60} h` } : value;
});

/** The day the intervention usually runs on: arrival, departure, the hours. */
export function ScheduleSection(): JSX.Element {
  const { t } = useLingui();
  const locale = useIntlLocale();

  const arrival = useField(schedule.arrival);
  const departure = useField(schedule.departure);
  const rideDuration = useField(intervention.rideDuration);

  const [hours, setHours] = useAtom(schedule.hours.valueAtom);

  const travelError = arrival.error ?? departure.error;

  return (
    <SectionFrame icon="Clock" legend={t`Base schedule`}>
      <div className={styles.body}>
        <div className={styles.row}>
          <FieldContext.Provider value={inline}>
            <Field invalid={Boolean(travelError)} className={styles.times}>
              <FieldLabel>{t`Arrival / departure`}</FieldLabel>

              <FieldContent>
                <TimePicker
                  range
                  layout="text"
                  minuteStep={STEP}
                  value={[arrival.value ?? null, departure.value ?? null]}
                  onValueChange={([begin, end]) => {
                    arrival.setValue(begin);
                    departure.setValue(end);
                  }}
                >
                  <TimePickerTrigger>
                    <TimePickerValue />
                  </TimePickerTrigger>

                  <TimePickerContent />
                </TimePicker>

                <FieldError match={Boolean(travelError)}>
                  {travelError}
                </FieldError>
              </FieldContent>
            </Field>
          </FieldContext.Provider>

          {/* Five minutes at a time, as the legacy dial went. The slider
              names itself over its track, its value at the other end. */}
          <Slider
            min={0}
            max={RIDE_MAX}
            step={STEP}
            value={rideDuration.value ?? 0}
            onValueChange={(next) =>
              rideDuration.setValue(0 === next ? null : next)
            }
            format={{ style: "unit", unit: "minute" }}
            locale={locale}
          >
            <SliderLabel>{t`Travel`}</SliderLabel>
            <SliderValue />
            <SliderControl>
              <SliderTrack>
                <SliderIndicator />
                <SliderMarks marks={RIDE_MARKS} />
                <SliderThumb tooltip="auto" />
              </SliderTrack>
            </SliderControl>
          </Slider>
        </div>

        <HoursField
          rows={hours}
          onRowsChange={setHours}
          create={({ begin, end }) => ({ id: null, begin, end })}
          label={t`Hours`}
          hint={<Trans>No range yet.</Trans>}
        />
      </div>
    </SectionFrame>
  );
}
